import React, { useEffect, useMemo, useRef } from "react";
import styles from "./Style.module.css";
import { useGetBookingSlots } from "../../APICalling/useApiQueries";
import TimeSlotsLoader from "./TimeSlotsLoader";
import { NoAvailability } from "../../Components/NoAvailability/NoAvailability";
import { AvailabilityError } from "../../Components/NoAvailability/AvailabilityError";
import { buildPaxesParam, formatDate } from "../../Utils/Helper";
import { resolveBookingDateTime } from "../../Utils/resolveBookingDateTime";
import { normalizeBookingSlots } from "../../Utils/normalizeBookingSlots";
import { BtnPrimary } from "../../Components/Buttons/BtnPrimary";

const formatSlotDate = (date) => {
  const { dayName, dayNum, monthName } = formatDate(date);
  return `${dayName}, ${dayNum} ${monthName}`;
};

export const TimeSlotsPicker = ({
  rescheduling = false,
  uuid = null,
  selectedOutletID,
  members = [],
  selectedDate,
  selectedTime,
  phoneNum,
  whatsappNum,
  onTimeSelect,
  onContinue,
  className = "",
  isWaitingForDates = false,
  isDateAvailable = true,
  isSlotChecking = false,
  datesError = "",
  onRetryDates,
}) => {
  const summaryEndRef = useRef(null);

  const paxesParam = useMemo(
    () => buildPaxesParam(members),
    [members]
  );

  const slotParams = useMemo(() => ({
    panel: "customer",
    outlet: selectedOutletID,
    start_date: selectedDate,
    end_date: selectedDate,
    paxes: paxesParam,
    ...(rescheduling && {
      type: "reschedule",
      uuid,
    }),
  }), [selectedOutletID, selectedDate, paxesParam, rescheduling, uuid]);

  // ONLY fetch time slots AFTER day-slot-availability completes successfully
  const { data: slotsData, isLoading, isFetching, isError, error, refetch } = useGetBookingSlots(slotParams, {
    enabled: !!selectedDate && !!selectedOutletID && !isWaitingForDates && isDateAvailable,
  });

  const slotGroups = useMemo(
    () => normalizeBookingSlots(selectedDate, slotsData?.data?.[selectedDate]),
    [selectedDate, slotsData]
  );
  const timeSlots = [...slotGroups.sameDay, ...slotGroups.nextDay];
  const selectedSlot = resolveBookingDateTime(selectedDate, selectedTime).slotLabel;
  const selectedAppointment = timeSlots.find((slot) => slot.slotLabel === selectedSlot);
  // A failed request is an error with a retry, never the "No Availability" state.
  const errorMessage = datesError ||
    (isError ? error?.response?.data?.message || "Unable to load available slots. Please try again." : "");
  // Resolved empty results stay visible during background refreshes.
  const isWaitingForSlots = isDateAvailable && !slotsData && (isLoading || isFetching);
  const nextDaySelection = selectedAppointment?.isNextDay ? selectedAppointment.slotLabel : "";
  const showSelectionSummary = Boolean(nextDaySelection) && isDateAvailable &&
    !isWaitingForDates && !isWaitingForSlots && !errorMessage;

  useEffect(() => {
    if (!showSelectionSummary) return;
    // Scroll past the banner's natural position. Targeting the sticky banner
    // itself would do nothing while it is already pinned inside the viewport.
    const frame = window.requestAnimationFrame(() => {
      summaryEndRef.current?.scrollIntoView({
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ? 'instant' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedDate, nextDaySelection, showSelectionSummary]);

  const renderSlots = (slots) => (
    <div className={styles.timeSlots}>
      {slots.map((slot) => (
        <button
          key={slot.slotLabel}
          type="button"
          disabled={isSlotChecking}
          className={`${styles.timeSlot} ${selectedSlot === slot.slotLabel ? styles.selected : ""} btn`}
          aria-pressed={selectedSlot === slot.slotLabel}
          aria-label={slot.isNextDay ? `${slot.displayTime}, next day (${slot.date})` : undefined}
          onClick={() => onTimeSelect(slot.originalSlot)}
        >
          {slot.displayTime.replace(' ', '')}
        </button>
      ))}
    </div>
  );

  return (
    <div className={`${styles.timePicker} ${rescheduling ? styles.footerSpace : ""} space-x space-y container-md ${className}`.trim()}>
      {isWaitingForDates || isWaitingForSlots ? (
        <TimeSlotsLoader />
      ) : errorMessage ? (
        <AvailabilityError
          message={errorMessage}
          onRetry={datesError ? onRetryDates : refetch}
        />
      ) : (
      isDateAvailable && timeSlots.length > 0 ?
        <>
          {slotGroups.sameDay.length > 0 && (
            <section aria-label="Same-day slots">
              {/* <h2 className={styles.slotGroupHeading}>{formatSlotDate(selectedDate)}</h2> */}
              {renderSlots(slotGroups.sameDay)}
            </section>
          )}
          {slotGroups.nextDay.length > 0 && (
            <section className={slotGroups.sameDay.length > 0 ? styles.nextDayGroup : undefined} aria-label="After-midnight slots">
              <h2 className={styles.slotGroupHeading}>
                <span aria-hidden="true">🌙 </span> <strong>After midnight </strong>· {formatSlotDate(slotGroups.nextDay[0].date)}
              </h2>
              {renderSlots(slotGroups.nextDay)}
            </section>
          )}
          {selectedAppointment?.isNextDay && (
            <>
              <div className={styles.slotSelectionSummary}>
                <p role="status" aria-live="polite">
                  {selectedAppointment.isNextDay && <span aria-hidden="true">🌙 </span>}
                  You're booking <strong>{formatSlotDate(selectedAppointment.date)} at {selectedAppointment.displayTime.replace(' ', '')}</strong>
                  {selectedAppointment.isNextDay && ' (next day)'}
                </p>
                {onContinue && (
                  <BtnPrimary label={isSlotChecking ? 'Checking…' : 'Continue'}
                    handleClick={onContinue} disabled={isSlotChecking} nativeDisabled noIcon
                    customClass={styles.slotContinue} />
                )}
              </div>
              <div ref={summaryEndRef} className={styles.summaryScrollAnchor} aria-hidden="true" />
            </>
          )}
        </>
         : (
          <NoAvailability 
            phoneNum={phoneNum} 
            whatsappNum={whatsappNum} 
          />
        )
      )}
    </div>
  );
};
